<template>
  <div class="executive-summary-impact-grid mb-10">
    <div
      v-for="(row, index) in moduleRows"
      :key="index"
      class="executive-summary-impact-grid__row page-break-avoid"
    >
      <executive-summary-impact-card
        v-for="module in row"
        :key="module"
        :module="module"
        :disabled="disabledModules[module]"
        :impact="securityImpact?.[module]"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import ExecutiveSummaryImpactCard from "@/components/reports/executive-summary/ExecutiveSummaryImpactCard.vue";
import chunk from "lodash/chunk";
import { EXECUTIVE_SUMMARY_MODULE_ORDER } from "@/constants/reports";
import type { TicketGeneratingSubscriptionModule } from "@/constants/workplaces";
import { isModuleDisabled } from "@/_helpers/subscription";
import type { ExecutiveSummarySecurityImpact } from "@/_store/reports.module";

// Two cards per row, matching the mock's two-column grid. Chunked at module level rather than
// in a computed because the order is a constant — no prop or store value can change it.
const IMPACT_CARDS_PER_ROW = 2;
const IMPACT_CARD_ROWS = chunk(EXECUTIVE_SUMMARY_MODULE_ORDER, IMPACT_CARDS_PER_ROW);

export default defineComponent({
  components: { ExecutiveSummaryImpactCard },
  props: {
    // Not required: the template already optional-chains it (`securityImpact?.[module]`) because
    // the whole object is a new backend field, and the tab renders this grid with no `v-if` — so
    // a payload without it logged "Missing required prop" on every render while the cards
    // themselves handled the absence correctly.
    securityImpact: {
      type: Object as PropType<ExecutiveSummarySecurityImpact>,
      default: undefined,
    },
  },
  setup() {
    // Memoised like the two sibling sections do it: the result depends only on the workspace
    // subscription, not on this component's props, so calling it per row re-walked the store for
    // an answer that cannot differ between rows of one render.
    const disabledModules = computed(
      () =>
        Object.fromEntries(
          EXECUTIVE_SUMMARY_MODULE_ORDER.map((module) => [module, isModuleDisabled(module)])
        ) as Record<TicketGeneratingSubscriptionModule, boolean>
    );

    return { moduleRows: IMPACT_CARD_ROWS, disabledModules };
  },
});
</script>

<style scoped lang="scss">
// Rows inside a block container rather than one flat grid. html2pdf pushes an element to the
// next page by inserting a spacer div before it, and a spacer inserted into a grid becomes a
// grid item: it consumes a cell instead of displacing the flow, so the card never moved down,
// stayed cut across the page boundary, and the grid grew an empty cell. Rows in normal flow let
// the spacer do its job, so a page break lands between cards instead of through one.
.executive-summary-impact-grid {
  &__row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }
}
</style>
